import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import "rxjs/add/operator/map";

import { SV_URL } from "../../@core/utils/utils";
import { Router } from "@angular/router";
import { UtilsProvider } from "./utils.service";
import { CredentiaisService } from "../../@core/utils/credentials.service";

/**
 * Serviço responsável por gerenciar as permissões (roles) dos usuários.
 *
 * Comunica-se com o backend para buscar, editar e criar permissões.
 * As permissões são armazenadas como um objeto JSON aninhado por segmento no backend.
 */
@Injectable({ providedIn: 'root' })
export class UserRoleProvider {

  /**
   * Mapeamento de nomes legados de permissão para caminhos no JSON aninhado.
   * Permite que chamadas legadas (ex: getUserRole('1', 'cheque')) continuem funcionando.
   */
  private static LEGACY_MAP: { [key: string]: string } = {
    premios_geral: 'vendas.premios',
    premios_lancamento: 'lancamentos.premios',
    rcc: 'vendas.rcc',
    deu: 'vendas.deu',
    deu_aberto: 'vendas.deu_aberto',
    rela_deu_aberto: 'relatorios.deu_aberto',
    vendedores: 'vendas.vendedores',
    envio: 'vendas.envio',
    cheque: 'vendas.cheque',
    folha: 'vendas.folha',
    folgas: 'vendas.folgas',
    pecas_lancamento: 'lancamentos.pecas',
    pecas_v2: 'lancamentos.pecas_v2',
    pecas_geral: 'vendas.pecas',
    descarga_geral: 'vendas.descarga',
    descarga_lancamento: 'lancamentos.descarga',
    mapa: 'lancamentos.mapa',
    resultados: 'lancamentos.resultados',
    rela_vendedores: 'relatorios.vendedores',
    rela_planilha_lot: 'relatorios.planilha',
    rela_premios: 'relatorios.premios',
    rela_premios_data: 'relatorios.premios_data',
    rela_descarga: 'relatorios.descarga',
    rela_deu: 'relatorios.deu',
    rela_envio: 'relatorios.envio',
    rela_coringa: 'relatorios.coringa',
    rela_rcc: 'relatorios.rcc',
    rela_cheque_rl: 'relatorios.cheque',
    rela_despesas: 'relatorios.despesas',
    rela_conferencia: 'relatorios.conferencia',
    despesas_geral: 'vendas.despesas',
    admin_check: 'gestao.autorizacoes',
    aut_deu: 'autorizacoes.deu',
    aut_cheque: 'autorizacoes.cheque',
    aut_premio: 'autorizacoes.premio',
    aut_envio: 'autorizacoes.envio',
    aut_coringa: 'autorizacoes.coringa',
    cambio: 'cambio.acesso',
    fotos: 'astral.fotos',
    astuser: 'astral.usuarios',
    horarios: 'vendas.folgas',
  };

  constructor(
    public http: HttpClient,
    public router: Router,
    public utils: UtilsProvider,
    private credentials: CredentiaisService
  ) {}

  /**
   * Busca as permissões de um usuário e verifica uma permissão específica.
   * Suporta tanto nomes legados (ex: 'cheque') quanto dot notation (ex: 'vendas.cheque').
   *
   * @param user - Identificador (não utilizado, busca pelo usuário logado)
   * @param rotina - Chave da permissão (legado ou dot notation)
   * @returns Promise que resolve com o valor da permissão (boolean)
   */
  getUserRole(user: any, rotina: string): Promise<any> {
    return new Promise((resolve) => {
      const url: string = SV_URL + "/getUserRole";
      const postParams = {
        user: this.credentials.getUser(),
      };

      this.utils
        .post(`${url}`, { pld: btoa(JSON.stringify(postParams)) })
        .subscribe({
          next: (res: any) => {
            if (res && res[0]) {
              const perms = res[0].permissions || {};
              const path = UserRoleProvider.LEGACY_MAP[rotina] || rotina;
              const parts = path.split('.');
              let current: any = perms;
              for (const part of parts) {
                if (!current || typeof current !== 'object') { resolve(false); return; }
                current = current[part];
              }
              resolve(!!current);
            } else {
              resolve(false);
            }
          },
          error: () => {
            sessionStorage.removeItem("portal");
            sessionStorage.removeItem("uid");
            sessionStorage.removeItem("x");
            this.router.navigate(["/pages/authLogin/login"]);
            window.location.reload();
          },
        });
    });
  }

  /**
   * Atualiza todas as permissões de um usuário enviando como objeto JSON aninhado.
   *
   * @param userid - ID do usuário cujas permissões serão atualizadas
   * @param permissions - Objeto aninhado { segmento: { header, ...funcionalidades } }
   * @returns Promise<void>
   */
  edit(userid: any, permissions: any): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const url: string = SV_URL + "/editUserRole";

      const postParams = {
        userid: userid,
        permissions: permissions,
      };

      this.http.put(url, postParams).subscribe(
        () => resolve(),
        (error) => reject(error)
      );
    });
  }

  /**
   * Cria um registro de permissões para um novo usuário.
   *
   * @param userid - ID do usuário recém-criado
   * @param permissions - Objeto aninhado de permissões (pode ser {} para tudo zerado)
   * @returns Promise<void>
   */
  create(userid: any, permissions: any): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const url: string = SV_URL + "/createUserRole";

      const postParams = {
        userid: userid,
        permissions: permissions || {},
      };

      this.http.post(url, postParams).subscribe(
        () => resolve(),
        (error) => reject(error)
      );
    });
  }
}
